import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { randomNonce } from '@/lib/nonce';

function isLocalDevelopmentOrigin(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function shouldEnableGoogleOneTap(clientId: string | undefined): boolean {
  if (!clientId) return false;
  if (typeof window === 'undefined') return false;

  const allowDev = import.meta.env.VITE_GOOGLE_ONE_TAP_ALLOW_DEV === 'true';
  if (import.meta.env.DEV && !allowDev && isLocalDevelopmentOrigin(window.location.hostname)) {
    if (import.meta.env.DEV) {
      console.info('Google One Tap disabled on local development origin. Set VITE_GOOGLE_ONE_TAP_ALLOW_DEV=true only if the Google OAuth origin is explicitly authorized.');
    }
    return false;
  }

  return true;
}

function loadGisScript(): Promise<void> {
  return new Promise((resolve) => {
    const g: any = (window as any).google;
    if (g?.accounts?.id) return resolve();
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      if ((existing as any).readyState === 'loaded') resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

export function useGoogleOneTap({ enabled = true }: { enabled?: boolean }) {
  const shownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let unsub: (() => void) | undefined;
    async function init() {
      if (!enabled || shownRef.current) return;
      const { data } = await supabase.auth.getSession();
      if (data.session) return; // no mostrar si ya está autenticado

      // Respeta una ventana de supresión (p.ej., tras iniciar sesión o registrarse)
      try {
        const suppressUntilRaw = localStorage.getItem('rc:oneTap:suppressUntil') || '0';
        const suppressUntil = Number(suppressUntilRaw) || 0;
        if (Date.now() < suppressUntil) return;
      } catch {}

      await loadGisScript();
        const g: any = (window as any).google;
        if (cancelled || !g) return;

      const client_id = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
        if (!shouldEnableGoogleOneTap(client_id)) return;

      const nonce = randomNonce();
        g.accounts.id.initialize({
        client_id,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
        ux_mode: 'popup',
        nonce,
        callback: async (resp: any) => {
          try {
            const id_token = resp?.credential as string;
            if (!id_token) return;
            const { error } = await supabase.auth.signInWithIdToken({
              provider: 'google',
              token: id_token,
              nonce,
            });
            if (error) throw error;
            // Suprimir futuros prompts por un tiempo y cerrar el actual
            try {
              const sixHours = 6 * 60 * 60 * 1000;
              localStorage.setItem('rc:oneTap:suppressUntil', String(Date.now() + sixHours));
              g.accounts.id.cancel?.();
              g.accounts.id.disableAutoSelect?.();
            } catch {}
          } catch (e) {
            // Log suave; no spam
            if (import.meta.env.DEV) console.warn('One Tap → Supabase error', e);
          }
        },
      });

      const btnEl = document.getElementById('gsi-btn');
      if (btnEl) {
        // Evitar mostrar el botón flotante en móviles; ocultar contenedor
        const isMobile = typeof window !== 'undefined' && (window.matchMedia?.('(max-width: 767px)').matches ?? false);
        if (isMobile) {
          try { (btnEl as HTMLElement).style.display = 'none'; } catch {}
        } else {
        try {
          g.accounts.id.renderButton(btnEl, {
            type: 'standard', shape: 'pill', size: 'large', text: 'signin_with', logo_alignment: 'left',
          });
        } catch {}
        }
      }

      try {
        g.accounts.id.prompt((n: any) => {
          try {
            if (n.isNotDisplayed && n.isNotDisplayed()) console.debug('OneTap not displayed:', n.getNotDisplayedReason?.());
            if (n.isSkippedMoment && n.isSkippedMoment()) console.debug('OneTap skipped:', n.getSkippedReason?.());
            if (n.isDismissedMoment && n.isDismissedMoment()) console.debug('OneTap dismissed:', n.getDismissedReason?.());
          } catch {}
        });
      } catch {}

      // Cierra el prompt si el usuario inicia sesión por cualquier vía en tiempo de ejecución
      try {
        const sub = supabase.auth.onAuthStateChange((_evt, session) => {
          if (session) {
            try {
              g.accounts.id.cancel?.();
              g.accounts.id.disableAutoSelect?.();
              const sixHours = 6 * 60 * 60 * 1000;
              localStorage.setItem('rc:oneTap:suppressUntil', String(Date.now() + sixHours));
              const btn = document.getElementById('gsi-btn');
              if (btn) { (btn as HTMLElement).style.display = 'none'; btn.innerHTML = ''; }
            } catch {}
          }
        });
        unsub = () => {
          try { sub.data.subscription.unsubscribe(); } catch {}
        };
      } catch {}

      shownRef.current = true;
    }

    init();
    return () => {
      cancelled = true;
      try {
        const g: any = (window as any).google;
        g?.accounts?.id?.cancel?.();
      } catch {}
      try {
        const btn = document.getElementById('gsi-btn');
        if (btn) { (btn as HTMLElement).style.display = 'none'; btn.innerHTML = ''; }
      } catch {}
      try { unsub?.(); } catch {}
    };
  }, [enabled]);
}
